import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/Layout/AppLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProjectView from './pages/ProjectView';
import AdminPanel from './pages/AdminPanel';
import EmailSettings from './pages/EmailSettings';
import CvScreening from './pages/CvScreening';
import FormBuilder from './pages/FormBuilder';
import FormSubmissions from './pages/FormSubmissions';
import CandidateForm from './pages/CandidateForm';
import InterviewScheduling from './pages/InterviewScheduling';
import CandidateScheduling from './pages/CandidateScheduling';
import HRReports from './pages/HRReports';
import FinanceReports from './pages/FinanceReports';
import CallMonitor from './pages/CallMonitor';
import CalendarDashboard from './pages/CalendarDashboard';
import CalendarSettings from './pages/CalendarSettings';
import Teams from './pages/Teams';
import Profile from './pages/Profile';

function LoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading, hasFeature } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasFeature('admin_panel')) return <Navigate to="/" replace />;
  return children;
}

// Generic feature guard: redirect to the dashboard if the user hasn't been
// granted the required feature. Super admins always pass (see hasFeature).
function FeatureRoute({ feature, children }) {
  const { user, loading, hasFeature } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasFeature(feature)) return <Navigate to="/" replace />;
  return children;
}

// Call-center users can only see /call-monitor. Block everything else.
function NotForCallCenter({ children }) {
  const { isCallCenter } = useAuth();
  if (isCallCenter) return <Navigate to="/call-monitor" replace />;
  return children;
}

// Call-monitor page itself is restricted to super_admin + call_center.
function CallMonitorRoute({ children }) {
  const { user, loading, hasFeature } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasFeature('call_monitor')) return <Navigate to="/" replace />;
  return children;
}

function HomeRoute() {
  const { isCallCenter } = useAuth();
  if (isCallCenter) return <Navigate to="/call-monitor" replace />;
  return <Dashboard />;
}

function AppRoutes() {
  const { user, isCallCenter } = useAuth();
  // Where to send a logged-in user that hits /login
  const loggedInHome = isCallCenter ? '/call-monitor' : '/';

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={loggedInHome} replace /> : <Login />} />
      <Route path="/candidate-form/:token" element={<CandidateForm />} />
      <Route path="/schedule/:token" element={<CandidateScheduling />} />

      <Route path="/" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route index element={<HomeRoute />} />
        {/* Self-service profile — any logged-in user can edit their own */}
        <Route path="profile" element={<Profile />} />
        <Route path="project/:projectId" element={<NotForCallCenter><FeatureRoute feature="ai_projects"><ProjectView /></FeatureRoute></NotForCallCenter>} />
        {/* Settings moved to Admin → Integrations; these routes only forward OAuth redirects there. */}
        <Route path="email-settings" element={<EmailSettings />} />
        <Route path="calendar-settings" element={<CalendarSettings />} />
        <Route path="calendar" element={<NotForCallCenter><FeatureRoute feature="calendar"><CalendarDashboard /></FeatureRoute></NotForCallCenter>} />
        <Route path="teams" element={<NotForCallCenter><FeatureRoute feature="teams"><Teams /></FeatureRoute></NotForCallCenter>} />
        <Route path="cv-screening/:departmentId" element={<NotForCallCenter><FeatureRoute feature="hr_module"><CvScreening /></FeatureRoute></NotForCallCenter>} />
        <Route path="form-builder/:departmentId" element={<NotForCallCenter><FeatureRoute feature="hr_module"><FormBuilder /></FeatureRoute></NotForCallCenter>} />
        <Route path="form-submissions/:projectId" element={<NotForCallCenter><FeatureRoute feature="hr_module"><FormSubmissions /></FeatureRoute></NotForCallCenter>} />
        <Route path="interviews/:departmentId" element={<NotForCallCenter><FeatureRoute feature="hr_module"><InterviewScheduling /></FeatureRoute></NotForCallCenter>} />
        <Route path="reports/:departmentId" element={<NotForCallCenter><FeatureRoute feature="hr_module"><HRReports /></FeatureRoute></NotForCallCenter>} />
        <Route path="finance-reports/:departmentId" element={<NotForCallCenter><FeatureRoute feature="finance_module"><FinanceReports /></FeatureRoute></NotForCallCenter>} />
        <Route path="call-monitor" element={<CallMonitorRoute><CallMonitor /></CallMonitorRoute>} />
        <Route path="admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
